import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { EmptyState } from '../ui/empty-state';
import { Bell, AlertTriangle, Info, AlertCircle, CheckCircle } from 'lucide-react';

const AlertHistory = ({ reducedMotion }) => {
  const [loading, setLoading] = useState(true);
  const [alertHistory, setAlertHistory] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchAlertHistory();
  }, []);

  // TODO Phase Audit Fix B: wire to real /api/analytics/alert-history endpoint when backend implements it.
  const fetchAlertHistory = async () => {
    setLoading(true);
    try {
      // No real endpoint yet — leave data empty so the EmptyState renders.
      setAlertHistory([]);
    } catch (error) {
      console.error('Failed to fetch alert history:', error);
      setAlertHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'critical':
        return { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' };
      case 'warning':
        return { icon: AlertCircle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' };
      case 'info':
        return { icon: Info, color: 'text-ink-500', bg: 'bg-ink-500', border: 'border-ink-300 dark:border-ink-700' };
      default:
        return { icon: Bell, color: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/30' };
    }
  };

  const filteredAlerts = (alertHistory || []).filter(alert => {
    if (filter === 'all') return true;
    if (filter === 'unresolved') return !alert.resolved;
    return alert.severity === filter;
  });

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="alert-history-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
              <Bell className="w-5 h-5 text-yellow-400" />
              Alert History
            </CardTitle>
            <CardDescription className="text-slate-400 mt-1">
              Past alerts and incident timeline
            </CardDescription>
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-1.5 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-slate-300"
            data-testid="filter-select"
          >
            <option value="all">All Alerts</option>
            <option value="unresolved">Unresolved</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-slate-800/30 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : filteredAlerts.length > 0 ? (
          <div className="space-y-3">
            {filteredAlerts.map((alert, index) => {
              const config = getSeverityConfig(alert.severity);
              const Icon = config.icon;
              return (
                <div
                  key={alert.id}
                  className={`p-4 rounded-lg border ${config.bg} ${config.border}`}
                  style={{
                    animation: reducedMotion ? 'none' : `fadeIn 0.3s ease-out ${index * 0.05}s both`
                  }}
                  data-testid={`alert-item-${alert.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-start gap-3 flex-1">
                      <Icon className={`w-5 h-5 ${config.color} mt-0.5`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`${config.color} bg-opacity-20 uppercase text-xs`} data-testid="alert-severity">
                            {alert.severity}
                          </Badge>
                          {alert.resolved && (
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30" data-testid="alert-resolved-badge">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Resolved
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-100 font-medium" data-testid="alert-message">{alert.message}</p>
                        <p className="text-xs text-slate-400 mt-1">Rule: {alert.rule}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 whitespace-nowrap" data-testid="alert-timestamp">{alert.timestamp}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon={Bell}
            title="No data yet"
            description="Data will appear once it flows through."
          />
        )}
      </CardContent>
    </Card>
  );
};

export default AlertHistory;